// EXEMPT
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Root } from "react-dom/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import { setup } from "goober";
import { prefix } from "goober/prefixer";

import {
  ContentBody,
  ContentScroller,
  ExternalContainer,
  InternalContainer,
  RouteWrapper,
} from "@/__main__/App.style.jsx";
import { readState } from "@/__main__/app-state.mjs";
import { IS_APP, IS_NODE, IS_TESTING } from "@/__main__/constants.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import type { RouteState } from "@/__main__/router.mjs";
import router, {
  initializeRoute,
  listenRouterEvents,
  unlistenRouterEvents,
} from "@/__main__/router.mjs";
import {
  disableAppDraggable,
  enableAppDraggable,
  registerDismissSnackbarsOnRouteChange,
} from "@/app/actions.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { APP_SCROLLER } from "@/app/constants.mjs";
import {
  registerCurrentGameListener,
  unregisterCurrentGameListener,
} from "@/app/current-game-listener.mjs";
import ErrorBoundary from "@/app/ErrorBoundary.jsx";
import GlobalNetworkActivity from "@/app/GlobalNetworkActivity.jsx";
import GlobalStyleInjector from "@/app/GlobalStyleInjector.jsx";
import LiveGameAcceptMatchmaking from "@/app/LiveGameAcceptMatchmaking.jsx";
import OnboardWidget from "@/app/OnboardWidget.jsx";
import appRefs from "@/app/refs.mjs";
import RouteErrorNotification from "@/app/RouteErrorNotification.jsx";
import { syncHardwareAcceleration } from "@/hub-settings/actions.mjs";
import {
  registerSettingsSubscriptions,
  unregisterSettingsSubscriptions,
} from "@/hub-settings/subscriptions.mjs";
import { registerMarketingSnackbars } from "@/marketing/snackbar.mjs";
import { EVENT_SHOW_SEARCH, hideSearch } from "@/search/actions.mjs";
import {
  useShouldShowAppNavigation,
  useShouldShowPageHeader,
} from "@/util/app-route.mjs";
import { classNames } from "@/util/class-names.mjs";
import ClientOnly from "@/util/ClientOnly.jsx";
import {
  appContainer,
  hasMarkup,
  INTERNAL_CONTAINER_ID,
  internalContainerClass,
} from "@/util/constants.mjs";
import { devError } from "@/util/dev.mjs";
import { CONTAINER_ID, OBSERVE_CLASS } from "@/util/exit-transitions.mjs";
import {
  observe as observeFallbackImg,
  unobserve as unobserveFallbackImg,
} from "@/util/fallback-img.mjs";
import { subscribeKvToGameState } from "@/util/game-handlers.mjs";
import {
  attachKeyListeners,
  removeKeyListeners,
} from "@/util/global-key-listeners.mjs";
import globals from "@/util/global-whitelist.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import {
  observe as observeLinks,
  unobserve as unobserveLinks,
} from "@/util/process-links.mjs";
import { reactNodeKey } from "@/util/react.mjs";
import { useRoute, useRouteComponent } from "@/util/router-hooks.mjs";
import { attachListeners, removeListeners } from "@/util/tooltip.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";
import useDisableAnimations from "@/util/use-disable-animations.mjs";
import { useGameAccentStyle } from "@/util/use-game-accent-style.mjs";
import cmpRefs from "@/util/use-observe-cmp-banner-visibility.mjs";
import useOverlayStyle from "@/util/use-overlay-style.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

// This seems to be required by goober.
setup(React.createElement, prefix, undefined, (props: object) => {
  // Props prefixed with "$" will be passed to the styled component but removed
  // before being passed to the DOM element.
  // https://stackoverflow.com/a/66170576
  for (const prop in props) {
    if (!prop.startsWith("$")) continue;
    delete props[prop];
  }
});

const ShowRoute = () => {
  const RouteComponent = useRouteComponent();
  const { currentPath } = useRoute(
    (prev, next) => prev?.currentPath === next.currentPath,
  );
  const { ContentWrapper, HeaderWrapper, NonAppNavigation } = useSnapshot(
    mainRefs.components,
  );
  const { floatingElementsTop } = useSnapshot(mainRefs.components);

  // Check the type of the RouteComponent. If it is a DOM node,
  // wrap it in a container.
  const containerRef = useRef<HTMLDivElement>();
  const routeContainer = useMemo(
    () =>
      "nodeType" in RouteComponent ? (
        <div className="flex-align-stretch" ref={containerRef} />
      ) : (
        <RouteComponent />
      ),
    [RouteComponent],
  );

  useClientLayoutEffect(() => {
    if ((RouteComponent as unknown as Node).nodeType) {
      containerRef.current?.appendChild(RouteComponent as unknown as Node);
    }

    // we want our modal to close as the route component is transitioning in
    hideSearch();

    eventBus.emit(EVENT_RENDER_ROUTE_COMPONENT);
  }, [RouteComponent]);

  // also close the search modal when the route changes
  useClientLayoutEffect(() => {
    hideSearch();
  }, [currentPath, containerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // This wrapper is used for implementing custom exit transitions.
  // See `exit-transitions.mjs`.
  const WrapperComponent = useMemo(() => {
    const Wrapper = () => (
      <RouteWrapper
        key={reactNodeKey(routeContainer)}
        className={OBSERVE_CLASS}
      >
        <LiveGameAcceptMatchmaking />
        {routeContainer}
      </RouteWrapper>
    );
    return Wrapper;
  }, [routeContainer]);

  const shouldShowPageHeader = useShouldShowPageHeader();
  const shouldShowAppNavigation = useShouldShowAppNavigation();

  // TODO: shouldShowPageHeader should not be used for this
  const clean =
    !shouldShowAppNavigation ||
    !shouldShowPageHeader ||
    router.RouteComponent?.fullBleed;

  // detect when the user scrolls to show the scrollbar
  useEffect(() => {
    const scroller = globals.document.getElementsByClassName(APP_SCROLLER)[0];
    if (!scroller) return;
    let t = null;
    let hovered = false;
    const SCROLL_TIMEOUT = 500;
    const showScroll = () => scroller.classList.add("scroll-active");
    const hideScroll = () => scroller.classList.remove("scroll-active");
    const queueHide = () => {
      clearTimeout(t);
      t = setTimeout(hideScroll, SCROLL_TIMEOUT);
    };

    const scrollHandler = () => {
      showScroll();
      if (!hovered) queueHide();
    };
    // this is the only way to get a hover handler for the scrollbar
    const moveHandler = (e: MouseEvent) => {
      const isScroller =
        e.target === scroller && e.clientX > scroller.clientWidth;

      if (isScroller) {
        if (hovered) return;
        clearTimeout(t);
        showScroll();
        hovered = true;
        disableAppDraggable();
        return;
      }
      if (hovered) {
        queueHide();
        hovered = false;
        enableAppDraggable();
      }
    };
    const passive = {
      passive: true,
    };
    scroller.addEventListener("scroll", scrollHandler, passive);
    scroller.addEventListener("mousemove", moveHandler, passive);
    return () => {
      scroller.removeEventListener("scroll", scrollHandler);
      scroller.removeEventListener("mousemove", moveHandler);
    };
  }, []);

  const { appShell } = useSnapshot(appRefs);

  return (
    <>
      {shouldShowPageHeader && (
        <HeaderWrapper>
          <appShell.ContentHeader />
        </HeaderWrapper>
      )}
      {shouldShowAppNavigation ? <appShell.AppHeaderNotification /> : null}
      {shouldShowAppNavigation ? <OnboardWidget /> : null}
      <ContentScroller {...classNames(APP_SCROLLER, clean && "full-bleed")}>
        <ContentBody id={CONTAINER_ID} data-portal-container="">
          <ErrorBoundary routeComponent={RouteComponent}>
            {shouldShowAppNavigation ? (
              <ContentWrapper>
                <WrapperComponent />
              </ContentWrapper>
            ) : (
              <NonAppNavigation>{routeContainer}</NonAppNavigation>
            )}
          </ErrorBoundary>
        </ContentBody>
        <ClientOnly>
          {shouldShowAppNavigation && (
            <>
              <appShell.Snackbar />
              <appShell.GamePromoFeatures />
            </>
          )}
        </ClientOnly>
        {floatingElementsTop.map((Child, i) => (
          <Child key={Child.name + i} />
        ))}
      </ContentScroller>
    </>
  );
};

const globalErrorTags = ["global_error_boundary"];

const App = () => {
  useOverlayStyle();
  useDisableAnimations();
  cmpRefs.useObserveCMPBannerVisibility();

  useClientLayoutEffect(() => {
    eventBus.emit(EVENT_APP_RENDER);

    // Used for notifying externally when the app has rendered.
    if (typeof globals.dispatchEvent === "function") {
      globals.dispatchEvent(new CustomEvent("__BLITZ_RENDER__"));
    }
  }, []);

  return (
    <ErrorBoundary tags={globalErrorTags} isGlobal>
      <GlobalStyleInjector />
      <AppContainer />
    </ErrorBoundary>
  );
};

function AppContainer() {
  useGameAccentStyle();

  const externalContainerClasses = useSnapshot(
    mainRefs.externalContainerClasses,
  );

  const internalContainerClasses = useSnapshot(
    mainRefs.internalContainerClasses,
  );

  const { appShell } = useSnapshot(appRefs);

  const shouldShowAppNavigation = useShouldShowAppNavigation();

  const [loadSearch, setLoadSearch] = useState(false);
  useEffect(() => {
    eventBus.on(EVENT_SHOW_SEARCH, () => {
      setLoadSearch(true);
    });
  }, []);

  const omniSearchRef = useCallbackRef((node: HTMLDivElement) => {
    (async () => {
      const _OmniSearchComponent = (await import("@/search/modal.mjs")).default;
      if (!_OmniSearchComponent) return;
      node.appendChild(_OmniSearchComponent);
    })();
  });

  return (
    <ExternalContainer {...classNames(...externalContainerClasses)}>
      <GlobalNetworkActivity />

      {IS_APP && !isRouteOverlay() && (
        <appShell.TitleBar className={internalContainerClass} />
      )}

      <InternalContainer
        id={INTERNAL_CONTAINER_ID}
        {...classNames(internalContainerClass, ...internalContainerClasses)}
        data-portal-container=""
      >
        <RouteErrorNotification />

        <ShowRoute />

        {shouldShowAppNavigation ? (
          <ClientOnly>
            {loadSearch && <div ref={omniSearchRef} />}
            <appShell.OmniSearchModal />
          </ClientOnly>
        ) : null}
      </InternalContainer>
    </ExternalContainer>
  );
}

export function createAppInstance() {
  // An interesting side effect of React.StrictMode is that it will
  // de-optimize the app by rendering everything twice, in order to detect
  // side effects and warn about those.
  return (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

export const EVENT_APP_RENDER = "EVENT_APP_RENDER";
export const EVENT_RENDER_ROUTE_COMPONENT = "EVENT_RENDER_ROUTE_COMPONENT";

let cleanupDismissSnackbarsOnRouteChange;

async function prepareMountApp(
  container: HTMLElement = appContainer,
  pathname?: string,
  search?: URLSearchParams,
  state?: RouteState,
): Promise<void> {
  mainRefs.didRender = new Promise<void>((resolve) => {
    eventBus.once(EVENT_APP_RENDER, () => {
      resolve();
    });
  });

  // Just a bunch of misc. functions to call on init.
  observeFallbackImg(container);
  observeLinks(container);
  attachKeyListeners();
  attachListeners(container);
  listenRouterEvents();
  registerSettingsSubscriptions(readState);
  registerMarketingSnackbars();
  cleanupDismissSnackbarsOnRouteChange =
    registerDismissSnackbarsOnRouteChange();
  // need to sync this to determine if animations should be disabled
  syncHardwareAcceleration();
  registerCurrentGameListener();
  blitzMessage(EVENTS.APP_MUTE);
  if (IS_APP) subscribeKvToGameState();

  const initialRoutePromise = initializeRoute(pathname, search, state);

  mainRefs.appInit = initialRoutePromise;

  try {
    await Promise.all([
      mainRefs.appInit,
      // Set up game integrations (app only! + testing)
      IS_APP || IS_TESTING
        ? import("@/__main__/ipc-game-integrations.mjs")
        : null,
    ]);
  } catch (error) {
    devError("MOUNT ERROR", error);
    if (IS_NODE) throw error;
  }

  if (!(container instanceof Node))
    throw new Error(`Invalid container node! ${container}`);
}

async function finalizeMountApp(
  app,
  container: HTMLElement = appContainer,
): Promise<Root> {
  let root: Root;

  if (hasMarkup) {
    root = hydrateRoot(container, app, {
      onRecoverableError(error) {
        devError("HYDRATE ERROR", error);
      },
    });
  } else {
    root = createRoot(container);
    root.render(app);
  }

  // This is needed because root.render is async and doesn't return anything...
  await mainRefs.didRender;

  // Finally call default fetchData after rendering.
  // This is to guarantee that the client doesn't hydrate w/ local data.
  if (hasMarkup) {
    const { route } = router;
    const { parameters, searchParams, state } = route;
    await mainRefs.defaultRoute.fetchData.call(
      route,
      parameters,
      searchParams,
      { ...state, isInitializing: false },
    );
  }

  // Remove extraneous styles on body element.
  globals.document.body.removeAttribute("style");

  // Waiting for the next tick should guarantee that DOM changes have been committed,
  // before finally considering that the app has mounted.
  await new Promise((resolve) => {
    if (typeof globals.requestIdleCallback === "function") {
      globals.requestIdleCallback(resolve);
    } else if (typeof globals.requestAnimationFrame === "function") {
      globals.requestAnimationFrame(resolve);
    } else {
      resolve(null);
    }
  });

  return root;
}

export async function mountApp(
  app: React.ReactNode,
  container: HTMLElement = appContainer,
  pathname?: string,
  search?: URLSearchParams,
  state?: RouteState,
) {
  await prepareMountApp(container, pathname, search, state);
  return finalizeMountApp(app, container);
}

export function unmountApp(root: Root, container: HTMLElement = appContainer) {
  unobserveFallbackImg();
  unobserveLinks();
  removeKeyListeners();
  removeListeners(container);
  unlistenRouterEvents();
  unregisterSettingsSubscriptions();
  unregisterCurrentGameListener();
  if (cleanupDismissSnackbarsOnRouteChange) {
    cleanupDismissSnackbarsOnRouteChange();
  }

  root.unmount();
}

export default App;
